import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { User, Session } from '@supabase/supabase-js';

interface Profile {
  id: string;
  email: string;
  fullName: string | null;
  telephone: string | null;
  role: 'user' | 'admin';
  maxStorageMB?: number;
  isActive: boolean;
  legal_agreement_version_accepted: number | null;
  forceAgreementAcceptance?: boolean;
  autoPlaylistEnabled?: boolean;
  youtubeDownloadEnabled?: boolean;
  subscriptionStatus?: 'free_trial' | 'active' | 'past_due' | 'locked' | 'canceled';
  trialEndsAt?: string | null;
  currentPeriodEndsAt?: string | null;
  subscriptionPackageId?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  isAdmin: boolean;
  isLocked: boolean;
  isImpersonating: boolean;
  returnToAdmin: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }

      if (!data) return null;

      const now = new Date();
      const trialEndsAt = data.trial_ends_at ? new Date(data.trial_ends_at) : null;
      const currentPeriodEndsAt = data.current_period_ends_at ? new Date(data.current_period_ends_at) : null;

      let effectiveStatus = data.subscription_status;
      if (data.role !== 'admin') {
        if (effectiveStatus === 'free_trial' && trialEndsAt && trialEndsAt < now) {
          effectiveStatus = 'locked';
          supabase.from('profiles').update({ subscription_status: 'locked' }).eq('id', data.id);
        }
        if (effectiveStatus === 'active' && currentPeriodEndsAt && currentPeriodEndsAt < now) {
          effectiveStatus = 'locked';
          supabase.from('profiles').update({ subscription_status: 'locked' }).eq('id', data.id);
        }
      }

      return {
        id: data.id,
        email: data.email,
        fullName: data.full_name,
        telephone: data.telephone,
        role: data.role,
        isActive: data.is_active,
        maxStorageMB: data.max_storage_mb,
        legal_agreement_version_accepted: data.legal_agreement_version_accepted,
        forceAgreementAcceptance: data.force_agreement_acceptance,
        autoPlaylistEnabled: data.auto_playlist_enabled,
        youtubeDownloadEnabled: data.youtube_download_enabled,
        subscriptionStatus: effectiveStatus,
        trialEndsAt: data.trial_ends_at,
        currentPeriodEndsAt: data.current_period_ends_at,
        subscriptionPackageId: data.subscription_package_id,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      } as Profile;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  };

  const createProfile = async (user: User, fullName?: string) => {
    try {
      const isSuperAdmin = user.email === 'admin@plexymusic.com';

      const { data: freeTrialPackage } = await supabase
        .from('subscription_packages')
        .select('id, trial_duration_days')
        .eq('name', 'Free Trial')
        .eq('is_active', true)
        .maybeSingle();

      const trialDays = freeTrialPackage?.trial_duration_days || 7;
      const trialEndsAt = new Date();
      trialEndsAt.setDate(trialEndsAt.getDate() + trialDays);

      const profileData = {
        id: user.id,
        email: user.email!,
        full_name: fullName || null,
        role: isSuperAdmin ? 'admin' as const : 'user' as const,
        is_active: true,
        max_storage_mb: 1000,
        subscription_package_id: freeTrialPackage?.id || null,
        subscription_status: isSuperAdmin ? 'active' : 'free_trial',
        trial_ends_at: isSuperAdmin ? null : trialEndsAt.toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('profiles')
        .upsert([profileData], { onConflict: 'id' })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          const existingProfile = await fetchProfile(user.id);
          if (existingProfile) return existingProfile;
        }
        console.error('Error creating profile:', error);
        return null;
      }

      return {
        id: data.id,
        email: data.email,
        fullName: data.full_name,
        telephone: data.telephone,
        role: data.role,
        isActive: data.is_active,
        maxStorageMB: data.max_storage_mb,
        legal_agreement_version_accepted: data.legal_agreement_version_accepted,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      } as Profile;
    } catch (error) {
      console.error('Error creating profile:', error);
      return null;
    }
  };

  useEffect(() => {
    const impersonationMode = sessionStorage.getItem('isImpersonating');
    const impersonatedUserId = sessionStorage.getItem('impersonatedUserId');
    const impersonatedEmail = sessionStorage.getItem('impersonatedEmail');

    if (impersonationMode === 'true' && impersonatedUserId && impersonatedEmail) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!session?.user) {
          console.warn('[Auth] Admin session expired while impersonating - clearing impersonation');
          sessionStorage.removeItem('isImpersonating');
          sessionStorage.removeItem('impersonatedUserId');
          sessionStorage.removeItem('impersonatedEmail');
          sessionStorage.removeItem('adminUserId');
          sessionStorage.removeItem('adminEmail');
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }

        console.log('[Auth] Impersonating user:', impersonatedEmail, 'with admin session:', session.user.email);

        const mockUser = {
          id: impersonatedUserId,
          email: impersonatedEmail,
          aud: 'authenticated',
          role: 'authenticated',
          created_at: new Date().toISOString(),
          app_metadata: {},
          user_metadata: {}
        } as User;

        setUser(mockUser);
        setIsImpersonating(true);

        fetchProfile(impersonatedUserId).then(userProfile => {
          setProfile(userProfile);
          setLoading(false);
        });
      });
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);

      if (session?.user) {
        fetchProfile(session.user.id).then(async (userProfile) => {
          if (!userProfile) {
            const newProfile = await createProfile(session.user);
            setProfile(newProfile);
          } else {
            setProfile(userProfile);
          }
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[Auth] onAuthStateChange event:', event, 'session:', session ? 'present' : 'null');

      if (event === 'SIGNED_OUT') {
        console.log('[Auth] User explicitly signed out');
        setUser(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      if (event === 'TOKEN_REFRESHED' && !session) {
        console.warn('[Auth] TOKEN_REFRESHED with null session - verifying session');
        supabase.auth.getSession().then(({ data: { session: verifiedSession } }) => {
          if (verifiedSession?.user) {
            console.log('[Auth] Session verified as still valid');
            setUser(verifiedSession.user);
            fetchProfile(verifiedSession.user.id).then(async (userProfile) => {
              if (!userProfile) {
                const newProfile = await createProfile(verifiedSession.user);
                setProfile(newProfile);
              } else {
                setProfile(userProfile);
              }
              setLoading(false);
            });
          } else {
            console.log('[Auth] Session confirmed invalid');
            setUser(null);
            setProfile(null);
            setLoading(false);
          }
        });
        return;
      }

      if (session?.user) {
        setUser(session.user);
        fetchProfile(session.user.id).then(async (userProfile) => {
          if (!userProfile) {
            const newProfile = await createProfile(session.user);
            setProfile(newProfile);
          } else {
            setProfile(userProfile);
          }
          setLoading(false);
        });
      } else if (event === 'INITIAL_SESSION' && !session) {
        console.log('[Auth] No initial session - user not logged in');
        setUser(null);
        setProfile(null);
        setLoading(false);
      } else if (!session) {
        console.warn('[Auth] Unexpected null session for event:', event, '- verifying');
        supabase.auth.getSession().then(({ data: { session: verifiedSession } }) => {
          if (verifiedSession?.user) {
            console.log('[Auth] Session still valid despite null in event');
            setUser(verifiedSession.user);
            fetchProfile(verifiedSession.user.id).then(async (userProfile) => {
              if (!userProfile) {
                const newProfile = await createProfile(verifiedSession.user);
                setProfile(newProfile);
              } else {
                setProfile(userProfile);
              }
              setLoading(false);
            });
          } else {
            console.log('[Auth] Session confirmed invalid');
            setUser(null);
            setProfile(null);
            setLoading(false);
          }
        });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { error };
    } catch (error: any) {
      return { error };
    }
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName || null,
          },
        },
      });

      if (error) {
        console.error('SignUp error:', error);
        return { error };
      }

      if (data.user) {
        await createProfile(data.user, fullName);
      }

      return { error: null };
    } catch (error: any) {
      console.error('SignUp error:', error);
      return { error };
    }
  };

  const signOut = async () => {
    const isCurrentlyImpersonating = sessionStorage.getItem('isImpersonating') === 'true';

    if (isCurrentlyImpersonating) {
      sessionStorage.removeItem('isImpersonating');
      sessionStorage.removeItem('impersonatedUserId');
      sessionStorage.removeItem('impersonatedEmail');
      setIsImpersonating(false);
      window.location.reload();
      return;
    }

    setUser(null);
    setProfile(null);
    sessionStorage.clear();
    localStorage.removeItem('sb-sjsoruhkcwahkxaxgtry-auth-token');

    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      // Ignore errors - local state is already cleared
    }
  };

  const returnToAdmin = async () => {
    const adminUserId = sessionStorage.getItem('adminUserId');
    const adminEmail = sessionStorage.getItem('adminEmail');

    if (adminUserId && adminEmail) {
      sessionStorage.setItem('wasImpersonating', 'true');

      sessionStorage.removeItem('isImpersonating');
      sessionStorage.removeItem('impersonatedUserId');
      sessionStorage.removeItem('impersonatedEmail');

      const mockAdminUser = {
        id: adminUserId,
        email: adminEmail,
        aud: 'authenticated',
        role: 'authenticated',
        created_at: new Date().toISOString(),
        app_metadata: {},
        user_metadata: {}
      } as User;

      setUser(mockAdminUser);
      setIsImpersonating(false);

      const adminProfile = await fetchProfile(adminUserId);
      setProfile(adminProfile);
    }
  };

  const refreshProfile = async () => {
    if (user) {
      const updatedProfile = await fetchProfile(user.id);
      setProfile(updatedProfile);
    }
  };

  const value = useMemo(() => ({
    user,
    profile,
    loading,
    signIn,
    signUp,
    signOut,
    refreshProfile,
    isAdmin: profile?.role === 'admin',
    isLocked: profile?.subscriptionStatus === 'locked',
    isImpersonating,
    returnToAdmin,
  }), [user, profile, loading, isImpersonating]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
